import {createContext, useCallback, useEffect, useState} from 'react';

import {FileImageVariant, Maybe, useSetTimeZone, useUserStateQueryLazyQuery} from '#apollo/schema';
import {FullscreenLoading} from '#component/partial';
import {useAuth} from '#hook';

interface UserStateContext {
  authenticatedUser?: {
    avatar?: Maybe<{id: string; url?: Maybe<string>}>;
    id: string;
    isVerified: boolean;
    userProfile?: Maybe<{initials?: Maybe<string>; email?: Maybe<string>}>;
    subscription?: Maybe<{hasActiveSubscription?: boolean; hasMembership?: boolean}>;
  };
  isAnonymous: boolean;
  isAuthenticated: boolean;
  isInitialLoad: boolean;
  isLoading: boolean;
  isVerified: boolean;
}

const DEFAULT_VALUE = {
  authenticatedUser: undefined,
  isAnonymous: true,
  isAuthenticated: false,
  isInitialLoad: true,
  isLoading: true,
  isVerified: false,
} as UserStateContext;

export const UserStateContext = createContext(DEFAULT_VALUE);

/**
 * The `UserStateProvider` provides context values related to a number of
 * possible user states:
 * - 'anonymous', meaning the user is not logged into the application.
 * - 'authenticated', meaning the user has logged into the application.
 * - 'verified', meaning the user has logged into the application, and has
 *   verified their account.
 *
 * It also provides an `isInitialLoad` flag, which begins as true when the
 * component is first mounted, and is set to false after the components initial
 * check for `isUserSignedIn` and subsequent user query to check verification
 * state resolves.
 */
export const UserStateProvider = ({children}: {children?: React.ReactNode}) => {
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const {isUserSignedIn, signInLoading} = useAuth();
  const [setTimeZone, {called}] = useSetTimeZone();
  const [userStateQuery, {data, loading}] = useUserStateQueryLazyQuery({
    variables: {imageVariant: FileImageVariant.Size_80x80},
  });
  const isAuthenticated = isUserSignedIn();
  const isAnonymous = !isAuthenticated;
  const isLoading = [isInitialLoad, signInLoading, loading].some(Boolean);
  const isVerified = !!data?.me?.isVerified && !isLoading && isAuthenticated;
  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // We should update the user's timezone when:
  const shouldUpdateTimeZone =
    // The user query has resolved successfully …
    !isLoading &&
    !!data?.me &&
    // … and the local timezone is different from the one set for the user …
    data?.me.timezone !== currentTimeZone &&
    // … and the user's timezone has not yet been updated this session.
    !called;

  const handleCheckSignIn = useCallback(async () => {
    const isAsyncAuthenticated = await isUserSignedIn({async: true});

    if (isAsyncAuthenticated) {
      await userStateQuery();
      setIsInitialLoad(false);
    } else {
      setIsInitialLoad(false);
    }
    // `isUserSignedIn` is listed as a dependency, so that we can re-query for
    // user on authentication state changes. Despite including it below, we use
    // an internal async check — we want to make sure loading states related to
    // authentication have fully resolved.
  }, [isUserSignedIn, userStateQuery]);

  if (shouldUpdateTimeZone) {
    try {
      setTimeZone({variables: {input: {timezone: currentTimeZone}}});
    } catch (e) {
      console.error('Failed to set timezone', e);
    }
  }

  useEffect(() => {
    handleCheckSignIn();
  }, [isAuthenticated, handleCheckSignIn]);

  return (
    <UserStateContext.Provider
      value={{
        authenticatedUser: data?.me,
        isAnonymous,
        isAuthenticated,
        isInitialLoad,
        isLoading,
        isVerified,
      }}>
      {isInitialLoad ? <FullscreenLoading /> : children}
    </UserStateContext.Provider>
  );
};
